import { defineStore } from 'pinia'
import ReportsService from '@/services/reports/ReportsService'
import ReportTemplateExportService from '@/services/reports/ReportTemplateExportService'
import type {
  ExecutionStatusResponse,
  ISODateTimeString,
  PreviewResponse,
  ReportExecution,
  ReportTemplate,
  ReportTemplateBinding,
  ReportInputSchema,
  ReportTemplateVersion,
  ReportVariable,
} from '@/types/reports'

type AutosaveConflict = {
  detail?: string
  current_updated_at: ISODateTimeString
}

type PendingAutosave = {
  templateId: string | number
  editorJson: any
  sourceJinja: string
}

let autosaveTimer: ReturnType<typeof setTimeout> | null = null

export const useReportsStore = defineStore('reports', {
  state: () => ({
    templates: [] as ReportTemplate[],
    templatesCount: 0,
    templatesTotalPages: 0,

    selectedTemplate: null as ReportTemplate | null,
    selectedTemplateVersions: [] as ReportTemplateVersion[],
    selectedTemplateBindings: [] as ReportTemplateBinding[],

    variables: [] as ReportVariable[],
    isLoadingVariables: false,

    isLoadingTemplates: false,
    isLoadingTemplate: false,

    isAutosaving: false,
    lastDraftUpdatedAt: null as ISODateTimeString | null,
    autosaveConflict: null as AutosaveConflict | null,
    pendingAutosave: null as PendingAutosave | null,

    preview: null as PreviewResponse | null,
    isLoadingPreview: false,

    executions: [] as ReportExecution[],
    executionsCount: 0,
    executionsTotalPages: 0,
    isLoadingExecutions: false,
  }),

  getters: {
    selectedTemplateId: (state) => state.selectedTemplate?.id ?? null,

    initialEditorJson: (state) => {
      const draftJson = state.selectedTemplate?.draft?.editor_json
      if (draftJson) return draftJson

      const publishedJson = state.selectedTemplate?.current_published_version?.editor_json
      if (publishedJson) return publishedJson

      return { type: 'doc', content: [] }
    },

    initialDraftUpdatedAt: (state) => state.selectedTemplate?.draft?.updated_at ?? null,
  },

  actions: {
    async fetchVariables(param?: string) {
      try {
        this.isLoadingVariables = true
        const resp = await ReportsService.getVariables(param)
        this.variables = resp.data?.variables || []
      } finally {
        this.isLoadingVariables = false
      }
    },

    async fetchTemplates(options?: { page?: number; pageSize?: number; search?: string }) {
      try {
        this.isLoadingTemplates = true
        const params = new URLSearchParams()
        if (options?.page) params.append('page', String(options.page))
        if (options?.pageSize) params.append('page_size', String(options.pageSize))
        if (options?.search) params.append('search', options.search)

        const qs = params.toString() ? `?${params.toString()}` : ''
        const resp = await ReportsService.listTemplates(qs)

        this.templates = resp.data?.results || []
        this.templatesCount = resp.data?.count ?? this.templates.length
        this.templatesTotalPages = resp.data?.total_pages ?? 1
      } catch (e) {
        console.error('[reportsStore.fetchTemplates] ❌', e)
        this.templates = []
      } finally {
        this.isLoadingTemplates = false
      }
    },

    async createTemplate(payload: { name: string; description?: string }) {
      const resp = await ReportsService.createTemplate(payload)
      return resp.data
    },

    async updateTemplateMeta(templateId: string | number, payload: Partial<ReportTemplate>) {
      const resp = await ReportsService.updateTemplate(templateId, payload)
      if (this.selectedTemplate && String(this.selectedTemplate.id) === String(templateId)) {
        this.selectedTemplate = { ...this.selectedTemplate, ...resp.data }
      }
      return resp.data
    },

    async deleteTemplate(templateId: string | number) {
      return ReportsService.deleteTemplate(templateId)
    },

    async fetchTemplate(templateId: string | number) {
      try {
        this.isLoadingTemplate = true
        this.preview = null
        this.autosaveConflict = null
        this.pendingAutosave = null

        const resp = await ReportsService.getTemplate(templateId)
        this.selectedTemplate = resp.data
        this.lastDraftUpdatedAt = resp.data?.draft?.updated_at ?? null

        return resp.data
      } finally {
        this.isLoadingTemplate = false
      }
    },

    async fetchTemplateDraft(templateId: string | number) {
      const resp = await ReportsService.getDraft(templateId)
      if (this.selectedTemplate && String(this.selectedTemplate.id) === String(templateId)) {
        this.selectedTemplate = { ...this.selectedTemplate, draft: resp.data.draft }
      }
      this.lastDraftUpdatedAt = resp.data.draft?.updated_at ?? null
      return resp.data.draft
    },

    async fetchTemplateBindings(templateId: string | number) {
      const resp = await ReportsService.getBindings(templateId)
      this.selectedTemplateBindings = resp.data?.bindings || []
      return this.selectedTemplateBindings
    },

    async replaceTemplateBindings(templateId: string | number, bindings: ReportTemplateBinding[]) {
      const resp = await ReportsService.replaceBindings(templateId, bindings)
      this.selectedTemplateBindings = resp.data?.bindings || []
      return this.selectedTemplateBindings
    },

    async fetchTemplateVersions(templateId: string | number) {
      const resp = await ReportsService.getVersions(templateId)
      this.selectedTemplateVersions = resp.data?.versions || []
      return this.selectedTemplateVersions
    },

    buildSourceJinja(editorJson: any): string {
      return ReportTemplateExportService.exportToSourceJinja(editorJson)
    },

    scheduleAutosave(templateId: string | number, editorJson: any, delayMs = 1500) {
      const sourceJinja = this.buildSourceJinja(editorJson)

      this.pendingAutosave = {
        templateId,
        editorJson,
        sourceJinja,
      }

      if (autosaveTimer) clearTimeout(autosaveTimer)
      autosaveTimer = setTimeout(() => {
        // fire-and-forget; UI can observe flags
        this.autosaveDraft(templateId, editorJson).catch(() => {
          // already logged
        })
      }, delayMs)
    },

    async autosaveDraft(
      templateId: string | number,
      editorJson: any,
      contract?: { input_schema?: ReportInputSchema; required_context_paths?: string[] },
    ) {
      const sourceJinja = this.buildSourceJinja(editorJson)

      this.pendingAutosave = {
        templateId,
        editorJson,
        sourceJinja,
      }

      try {
        this.isAutosaving = true
        this.autosaveConflict = null

        const resp = await ReportsService.autosaveDraft(templateId, {
          source_jinja: sourceJinja,
          editor_json: editorJson,
          expected_updated_at: this.lastDraftUpdatedAt || undefined,
          input_schema: contract?.input_schema,
          required_context_paths: contract?.required_context_paths,
        })

        const draft = resp.data?.draft
        if (this.selectedTemplate && String(this.selectedTemplate.id) === String(templateId)) {
          this.selectedTemplate = {
            ...this.selectedTemplate,
            draft: draft,
          }
        }

        this.lastDraftUpdatedAt = draft?.updated_at ?? this.lastDraftUpdatedAt
        this.pendingAutosave = null
        return draft
      } catch (e: any) {
        if (e?.response?.status === 409) {
          const conflict: AutosaveConflict = {
            detail: e?.response?.data?.detail,
            current_updated_at: e?.response?.data?.current_updated_at,
          }
          this.autosaveConflict = conflict
          console.warn('[reportsStore.autosaveDraft] ⚠️ conflict 409', conflict)
          return null
        }

        console.error('[reportsStore.autosaveDraft] ❌', e)
        throw e
      } finally {
        this.isAutosaving = false
      }
    },

    async resolveAutosaveConflict(strategy: 'reload' | 'overwrite') {
      if (!this.autosaveConflict || !this.pendingAutosave) return

      const { templateId, editorJson, sourceJinja } = this.pendingAutosave

      if (strategy === 'reload') {
        await this.fetchTemplate(templateId)
        this.autosaveConflict = null
        this.pendingAutosave = null
        return
      }

      // overwrite: accept server timestamp and retry once
      this.lastDraftUpdatedAt = this.autosaveConflict.current_updated_at
      this.autosaveConflict = null

      try {
        this.isAutosaving = true
        const resp = await ReportsService.autosaveDraft(templateId, {
          source_jinja: sourceJinja,
          editor_json: editorJson,
          expected_updated_at: this.lastDraftUpdatedAt || undefined,
        })

        const draft = resp.data?.draft
        if (this.selectedTemplate && String(this.selectedTemplate.id) === String(templateId)) {
          this.selectedTemplate = {
            ...this.selectedTemplate,
            draft: draft,
          }
        }

        this.lastDraftUpdatedAt = draft?.updated_at ?? this.lastDraftUpdatedAt
        this.pendingAutosave = null
      } catch (e) {
        console.error('[reportsStore.resolveAutosaveConflict] ❌', e)
        throw e
      } finally {
        this.isAutosaving = false
      }
    },

    async publish(templateId: string | number, payload?: { audience_policy?: any; data_scope_policy?: any }) {
      const resp = await ReportsService.publish(templateId, {
        ...payload,
        expected_draft_updated_at: this.lastDraftUpdatedAt || undefined,
      })
      // refresh detail to include new published version
      await this.fetchTemplate(templateId)
      return resp.data?.version
    },

    async rollback(templateId: string | number, versionId: string | number) {
      const resp = await ReportsService.rollback(templateId, { version_id: versionId })
      await this.fetchTemplate(templateId)
      return resp.data?.version
    },

    async previewTemplate(
      templateId: string | number,
      context: Record<string, any>,
      options?: { source?: 'published' | 'version' | 'draft'; versionId?: string | number },
    ) {
      try {
        this.isLoadingPreview = true
        const resp = await ReportsService.preview(templateId, {
          context,
          source: options?.source,
          version_id: options?.versionId,
        })
        this.preview = resp.data
        return resp.data
      } finally {
        this.isLoadingPreview = false
      }
    },

    async renderTemplate(templateId: string | number, context: Record<string, any>) {
      const resp = await ReportsService.render(templateId, { context })
      return resp.data?.execution_id
    },

    async fetchExecutions(options?: { page?: number; pageSize?: number }) {
      try {
        this.isLoadingExecutions = true
        const params = new URLSearchParams()
        if (options?.page) params.append('page', String(options.page))
        if (options?.pageSize) params.append('page_size', String(options.pageSize))

        const qs = params.toString() ? `?${params.toString()}` : ''
        const resp = await ReportsService.listExecutions(qs)
        this.executions = resp.data?.results || []
        this.executionsCount = resp.data?.count ?? this.executions.length
        this.executionsTotalPages = resp.data?.total_pages ?? 1
      } catch (e) {
        console.error('[reportsStore.fetchExecutions] ❌', e)
        this.executions = []
      } finally {
        this.isLoadingExecutions = false
      }
    },

    async getExecutionStatus(
      executionId: string | number,
      includeDownloadUrl?: boolean,
    ): Promise<ExecutionStatusResponse> {
      const resp = await ReportsService.getExecutionStatus(executionId, includeDownloadUrl)
      return resp.data
    },

    async getExecutionDownloadUrl(executionId: string | number): Promise<{ url: string; expires_in?: number }> {
      const resp = await ReportsService.getExecutionDownloadUrl(executionId)
      return resp.data
    },
  },
})
